import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';
import {
  useGetNotesQuery,
  useDeleteNoteMutation,
  useCreateNoteMutation,
} from '../../slices/notesApiSlice';
import { toast } from 'react-toastify';
import { useState, useMemo } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NoteListScreen = () => {
  const { pageNumber } = useParams();
  const { data, isLoading, error, refetch } = useGetNotesQuery({ pageNumber });
  const [deleteNote, { isLoading: loadingDelete }] = useDeleteNoteMutation();
  const [createNote, { isLoading: loadingCreate }] = useCreateNoteMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [date1, setDate1] = useState('2023-01-01');
  const [date2, setDate2] = useState(new Date().toISOString().split('T')[0]);

  const matchesSearchCriteria = (note) => {
    const noteDate = Date.parse(note.date);
    const start = Date.parse(date1);
    const end = Date.parse(date2);
     
    const matchesDate = noteDate >= start && noteDate < end;
    const matchesFilter =
      !filter ||
      note.status?.toLowerCase().includes(filter.toLowerCase()) ||
      note.username?.toLowerCase().includes(filter.toLowerCase()) ||
      note.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
      note.details?.toLowerCase().includes(filter.toLowerCase());

    return matchesDate && matchesFilter;
  };

  const filteredSortedNotes = useMemo(() => {
    if (!data?.notes) return [];
    let notes = data.notes.filter(matchesSearchCriteria);

    if (sortField) {
      notes.sort((a, b) => {
        if (sortField === 'number') {
          return sortOrder === 'asc' ? a.number - b.number : b.number - a.number;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          return sortOrder === 'asc'
            ? a[sortField].localeCompare(b[sortField])
            : b[sortField].localeCompare(a[sortField]);
        }
      });
    }

    return notes;
  }, [data, filter, date1, date2, sortField, sortOrder]);

  const totalNumber = data?.notes.reduce((acc, note) => acc + Number(note.number || 0), 0) || 0;
  const totalFilteredNumber = filteredSortedNotes.reduce((acc, note) => acc + Number(note.number || 0), 0);

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteNote(id);
        toast.success('Note deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const createNoteHandler = async () => {
    if (window.confirm('Are you sure you want to create a new note?')) {
      try {
        const newNote = {
          date: new Date().toISOString().split('T')[0],
          status: 'Pending',
          accountName: 'Default Account',
          details: 'New Note',
          number: 0,
        };
        await createNote(newNote).unwrap();
        toast.success('New note created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const exportFilteredToExcel = () => {
    if (!filteredSortedNotes.length) {
      toast.error('No filtered notes available to export!');
      return;
    }

    const notesData = filteredSortedNotes.map((note) => ({
      Date: new Date(note.date).toLocaleDateString(),
      Status: note.status,
      AccountName: note.accountName,
      UserName: note.username,
      Details: note.details,
      Number: note.number,
    }));

    const worksheet = XLSX.utils.json_to_sheet(notesData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Filtered Notes');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(file, 'Filtered_Admin_Notes.xlsx');
  };

  const downloadNoteImages = async (note) => {
    const zip = new JSZip();
    const folder = zip.folder(`Note_${note._id}`);

    const imagePromises = (note.images || []).map(async (imagePath) => {
      try {
        const response = await fetch(imagePath.replace(/\\/g, '/'));
        const blob = await response.blob();
        const filename = imagePath.split('/').pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }
    });

    await Promise.all(imagePromises);
    zip.generateAsync({ type: 'blob' }).then((zipFile) => {
      saveAs(zipFile, `Note_${note._id}_Images.zip`);
    });
  };

  const downloadAllImages = async () => {
    const zip = new JSZip();
    const folder = zip.folder('All_Notes_Images');

    const imagePromises = data.notes.flatMap((note) =>
      (note.images || []).map(async (imagePath) => {
        try {
          const response = await fetch(imagePath.replace(/\\/g, '/'));
          const blob = await response.blob();
          const filename = imagePath.split('/').pop();
          folder.file(filename, blob);
        } catch (error) {
          console.error(`Failed to fetch ${imagePath}`, error);
        }
      })
    );

    await Promise.all(imagePromises);
    zip.generateAsync({ type: 'blob' }).then((zipFile) => {
      saveAs(zipFile, 'All_Notes_Images.zip');
    });
  };

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Admin Notes</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3 mx-2" onClick={downloadAllImages}>
            <FaDownload /> Download All Images
          </Button>
          <Button className="my-3 mx-2" onClick={exportFilteredToExcel}>
            <FaDownload /> Export Filtered to Excel
          </Button>
          <Button className="my-3" onClick={createNoteHandler}>
            <FaPlus /> Create Note
          </Button>
          <Link to="/create/notification" className="btn btn-light my-3">
            Send Message
          </Link>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h5>Total Number (All Notes): {totalNumber}</h5>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="date1">
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={date1} onChange={(e) => setDate1(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="date2">
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={date2} onChange={(e) => setDate2(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <h5>Total Notes (Filtered): {totalFilteredNumber}</h5>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by status, account name, user, or details"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {(loadingCreate || loadingDelete || isLoading) && <Loader />}
      {error ? (
        <Message variant="danger">{error.data.message}</Message>
      ) : (
                <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  DATE {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>STATUS</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  ACCNAME {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                  USER {sortField === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  DETAILS {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('number')} style={{ cursor: 'pointer' }}>
                  NUMBER {sortField === 'number' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>IMAGES</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSortedNotes.map((note) => (
                <tr key={note._id}>
                  <td>{new Date(note.date).toLocaleDateString()}</td>
                  <td>{note.status}</td>
                  <td>{note.accountName}</td>
                  <td>{note.username}</td>
                  <td>{note.details}</td>
                  <td>{note.number}</td>
                  <td className="thumbnails">
                    {note.images?.length > 0 && (
                      <img
                        src={note.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <Link
                      className="btn btn-primary btn-sm m-2"
                      to={`/note/${note._id}`}
                      role="button"
                    >
                      Details
                    </Link>
                    <Button
                      as={Link}
                      to={`/admin/note/${note._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="info"
                      className="btn-sm mx-2"
                      onClick={() => downloadNoteImages(note)}
                    >
                      <FaDownload /> Images
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(note._id)}
                    >
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data?.pages && data?.page && (
  <Paginate pages={data.pages} page={data.page} isAdmin={true} type="notelist" />
     )}
        </>
      )}
    </>
  );
};

export default NoteListScreen;